import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { CareRequestContext } from '@care/fastify-context';
import logger from '@/lib/clientLogger';
import { useAppDispatch, useProviderMVState } from '@/components/AppState';
import { MultiVerticalServiceType } from '@/types/providerMV';
import {
  AUTHENTICATED_USER_SESSION_STATE,
  PROVIDER_MULTI_VERTICAL_ROUTES,
  PROVIDER_CHILD_CARE_ROUTES,
  PROVIDER_ROUTES,
} from '@/constants';
import useMultiVerticalRedirect from '@/components/hooks/useMultiVerticalRedirect';
import useGetProviderLocation from '@/components/hooks/useGetProviderLocation';
import { useRetrieveVhpMfeSharedData } from '@/components/hooks/useVhpMfeSharedData';

function useMVIndexRedirect({
  userCareSession,
  hasCSCSessionCookie,
}: {
  userCareSession: CareRequestContext['session'];
  hasCSCSessionCookie: boolean | undefined;
}) {
  const { redirectToEnrollmentStep } = useMultiVerticalRedirect();
  const [fetchUser, setFetchUser] = useState(false);
  const { zipcode } = useProviderMVState();
  const { setProviderLocation } = useGetProviderLocation();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { LOCATION } = PROVIDER_MULTI_VERTICAL_ROUTES;
  const { isFetching, data: vhpData } = useRetrieveVhpMfeSharedData();
  const isUserAuthenticated =
    hasCSCSessionCookie && userCareSession?.state === AUTHENTICATED_USER_SESSION_STATE;

  useEffect(() => {
    if (fetchUser) {
      setProviderLocation();
    }
  }, [fetchUser]);

  useEffect(() => {
    if (zipcode !== '' && isUserAuthenticated) {
      redirectToEnrollmentStep();
    }
  }, [zipcode]);

  useEffect(() => {
    const isOldCCFlow = router.asPath?.includes(PROVIDER_CHILD_CARE_ROUTES.INDEX);
    const isOldSCFlow = router.asPath?.includes(PROVIDER_ROUTES.INDEX);

    if (router.query.vertical?.length || isOldCCFlow || isOldSCFlow) {
      const vertical =
        router.query.vertical || (isOldCCFlow && 'CHILDCARE') || (isOldSCFlow && 'SENIRCARE');

      if (isOldCCFlow || isOldSCFlow) {
        logger.info({
          event: 'RequestToDeprecatedProviderFlow',
          vertical,
          currentURL: router.asPath,
          referrer: document.referrer,
        });
      }

      dispatch({
        type: 'setProviderMVServiceType',
        serviceType: vertical as MultiVerticalServiceType,
      });
      (async () => {
        if (!isUserAuthenticated) {
          if (isFetching) return;

          const { zipcodeData } = vhpData || {};
          if (zipcodeData) {
            dispatch({
              type: 'setProviderMVLocation',
              zipcode: zipcodeData.zipcode,
              city: zipcodeData.city,
              state: zipcodeData.state,
            });
          }
          router.push(LOCATION);
          return;
        }

        if (zipcode === '') {
          setFetchUser(true);
          return;
        }

        redirectToEnrollmentStep();
      })();
    }
  }, [router.query.vertical, router.asPath, isFetching]);
}

export default useMVIndexRedirect;
